import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import Popup from '@/components/Popup';
import { IS_FR } from '@/utils/constants';

interface PromotionData {
  type: 'seckill' | 'flash-sale' | 'promotion' | 'member' | 'no-member' | 'ordinary' | 'tourist';
  title?: string;
  rules?: string[];
  backgroundColor?: string;
  backgroundImage?: string;
}

interface PromotionRulesProps {
  promotionData: PromotionData;
  product: any;
  isWeb?: boolean;
  displayMode?: 'inline' | 'popup'; // 显示模式：inline 平铺显示，popup 弹窗显示
  onRulesClick?: () => void; // 点击规则时的回调（用于统计等）
}

export interface PromotionRulesRef {
  openPopup: () => void;
  hasRules: boolean;
}

// 判断是否有促销规则的辅助函数（供外部使用）
export const hasPromotionRules = (promotionData: PromotionData, product: any): boolean => {
  if (!['seckill', 'flash-sale', 'promotion'].includes(promotionData.type)) return false;

  // 活动规则文本
  if (
    [3, 4].includes(Number(product.products_promotions_type)) &&
    product.activity_style?.flash_killing_rules_text
  ) {
    return true;
  }
  // 限购规则
  if (product.limit_rule === 3 && product.limit_buy) {
    return true;
  }
  if (product.limit_rule === 2 && product.limit_buy) {
    return true;
  }

  // 第二件半价互斥
  if (product.exclusion_pc?.half_price === 1) {
    return true;
  }

  // 不退不换（总是有这条规则）
  return true;
};

// 促销规则组件
const PromotionRules = forwardRef<PromotionRulesRef, PromotionRulesProps>(
  ({ promotionData, product, isWeb, displayMode = 'inline', onRulesClick }, ref) => {
    const t = useTranslations('goodsDetail.promotionRules');
    const t2 = useTranslations('goodsDetail');
    const [showPopup, setShowPopup] = useState(false);

    const rules = [];

    // 只在符合条件的促销类型下收集规则
    if (['seckill', 'flash-sale', 'promotion'].includes(promotionData.type)) {
      // 活动规则文本
      if (
        [3, 4].includes(Number(product.products_promotions_type)) &&
        product.activity_style?.flash_killing_rules_text
      ) {
        rules.push(product.activity_style.flash_killing_rules_text);
      }
      // 限购规则
      else if (product.limit_rule === 3 && product.limit_buy) {
        rules.push(
          t('limitOfItemsPerOrder', {
            limit: product.limit_buy,
            item: product.limit_buy > 1 ? t('items') : t('item'),
          }),
        );
      } else if (product.limit_rule === 2 && product.limit_buy) {
        rules.push(
          t('limitOfItemsPerAccount', {
            limit: product.limit_buy,
            item: product.limit_buy > 1 ? t('items') : t('item'),
          }),
        );
      }

      if (IS_FR) {
        // fr 站需要获新，不搞那么多条条框框
        // 联系人 @wenxinke、李安然
        rules.push('Offre spéciale', 'Garantie 365 jours');
      } else {
        // 第二件半价互斥
        if (product.exclusion_pc?.half_price === 1) {
          rules.push(t('no2ndFrameHalfPriceOffer'));
        }

        // 不退不换
        rules.push(t('noReturnsOrExchanges'));
      }
    }

    // 通过 ref 暴露方法（必须在所有 hooks 调用之后，但在任何早期返回之前）
    useImperativeHandle(ref, () => ({
      openPopup: () => {
        if (displayMode === 'popup') {
          setShowPopup(true);
          onRulesClick?.();
        }
      },
      hasRules: rules.length > 0,
    }));

    // 早期返回检查（必须在所有 hooks 之后）
    if (!['seckill', 'flash-sale', 'promotion'].includes(promotionData.type)) return null;
    if (rules.length === 0) return null;

    // 弹窗模式
    if (displayMode === 'popup') {
      return (
        <Popup
          open={showPopup}
          onClose={() => setShowPopup(false)}
          position="bottom"
          height="auto"
          round
          borderRadius={16}
          title={t2('promoTerms')}
        >
          <div>
            {rules.map((rule, index) => (
              <div
                key={index}
                className="py-[12px] text-[14px] text-[#808080] leading-[20px] border-b border-[#f4f4f4] last:border-b-0"
              >
                {rule}
              </div>
            ))}
          </div>
        </Popup>
      );
    }

    // 平铺模式（默认）
    return (
      <div className={`${isWeb ? 'pt-16' : 'py-[6px]'}`}>
        <div
          className={`flex items-center ${isWeb ? 'gap-x-20' : 'bg-[#f4f4f4] py-[12px] rounded-[4px]'}`}
        >
          {rules.map((rule) => (
            <span
              key={rule}
              className={`flex-1 text-[#808080]  ${isWeb ? 'w-[33%] pr-20 leading-[16px] border-r border-[#d0d4db] last:border-r-0 text-[12px]' : 'px-[10px] text-center leading-[13px] text-[11px]'}`}
            >
              {rule}
            </span>
          ))}
        </div>
      </div>
    );
  },
);

PromotionRules.displayName = 'PromotionRules';

export default PromotionRules;
export type { PromotionData };
